import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../../Config";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PayrollMenu from "./PayrollMenu";

/*
 * The catalogue of additional duties / responsibilities - Wing Incharge, Class Teacher,
 * Subject Coordinator, HOD ... - and the monthly allowance each one carries.
 *
 * Employees are given these on the Edit Employee page. The allowance is recorded here
 * now; posting it to payroll is the next phase, and "Salary type" is the line it will
 * post to when that comes.
 */

const fmtAmount = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-PK"));

function ManageAdditionalDuties() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [salaryTypes, setSalaryTypes] = useState([]);

  const blank = {
    ID: 0,
    Role_Name: "",
    Description: "",
    Allowance_Value: "",
    Salary_Type_ID: "",
    Allow_Multiple: false,
    Requires_Details: false,
    Sort_Order: "",
    Is_Active: true,
  };
  const [showForm, setShowForm] = useState(false);
  const [entry, setEntry] = useState(blank);
  const [validated, setValidated] = useState(false);
  const [saving, setSaving] = useState(false);

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const load = useCallback(() => {
    setLoading(true);
    post("AdditionalDuty/GetDuties", auth({ IncludeInactive: true }))
      .then((res) => {
        if (res.data.status_code === 1) {
          setRows(res.data.ListofRecords || []);
          setSalaryTypes(res.data.SalaryTypes || []);
        } else {
          setRows([]);
        }
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [post, auth]);

  useEffect(() => {
    document.title = "Additional Duties";
    load();
  }, [load]);

  const set = (field, value) => setEntry((prev) => ({ ...prev, [field]: value }));

  /* ---- add / edit ---- */

  const openAdd = () => {
    const nextSort = rows.reduce((m, r) => Math.max(m, r.Sort_Order || 0), 0) + 10;
    setEntry({ ...blank, Sort_Order: nextSort });
    setValidated(false);
    setShowForm(true);
  };

  const openEdit = (d) => {
    setEntry({
      ID: d.ID,
      Role_Name: d.Role_Name || "",
      Description: d.Description || "",
      Allowance_Value: d.Allowance_Value ?? "",
      Salary_Type_ID: d.Salary_Type_ID ? String(d.Salary_Type_ID) : "",
      Allow_Multiple: !!d.Allow_Multiple,
      Requires_Details: !!d.Requires_Details,
      Sort_Order: d.Sort_Order ?? "",
      Is_Active: !!d.Is_Active,
    });
    setValidated(false);
    setShowForm(true);
  };

  const save = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "AdditionalDuty/SaveDuty",
      auth({
        ID: entry.ID,
        Role_Name: entry.Role_Name,
        Description: entry.Description,
        Allowance_Value: entry.Allowance_Value === "" ? 0 : parseFloat(entry.Allowance_Value),
        Salary_Type_ID: entry.Salary_Type_ID === "" ? 0 : parseInt(entry.Salary_Type_ID, 10),
        Allow_Multiple: entry.Allow_Multiple,
        Requires_Details: entry.Requires_Details,
        Sort_Order: entry.Sort_Order === "" ? null : parseInt(entry.Sort_Order, 10),
        Is_Active: entry.Is_Active,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          setShowForm(false);
          load();
        } else {
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setSaving(false));
  };

  /* ---- activate / delete ---- */

  const toggleActive = (d) => {
    post("AdditionalDuty/SetDutyActive", auth({ ID: d.ID, Is_Active: !d.Is_Active }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  const remove = (d) => {
    if (d.Holders > 0) {
      toast.error(
        `"${d.Role_Name}" is currently held by ${d.Holders} ${d.Holders === 1 ? "employee" : "employees"}. End those assignments first, or deactivate it instead.`,
        { position: toast.POSITION.TOP_RIGHT, autoClose: 8000 }
      );
      return;
    }
    if (!window.confirm(`Delete "${d.Role_Name}"?`)) return;
    post("AdditionalDuty/DeleteDuty", auth({ ID: d.ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, { position: toast.POSITION.TOP_RIGHT });
          load();
        } else {
          toast.error(res.data.status_message, { position: toast.POSITION.TOP_RIGHT, autoClose: 8000 });
        }
      })
      .catch(() => toast.error("Network error", { position: toast.POSITION.TOP_RIGHT }));
  };

  /* ---- render ---- */

  const renderRows = () => {
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center text-muted py-4">
            No responsibilities defined yet
          </td>
        </tr>
      );
    }

    return rows.map((d, i) => (
      <tr key={d.ID} className={d.Is_Active ? "" : "text-muted"}>
        <td>{i + 1}</td>
        <td>
          <span className="fw-bold">{d.Role_Name}</span>
          {d.Description ? <div className="small text-muted">{d.Description}</div> : null}
        </td>
        <td className="text-end text-nowrap">{fmtAmount(d.Allowance_Value)}</td>
        <td>{d.Salary_Type_Name || <span className="text-muted">—</span>}</td>
        <td className="text-center">
          {d.Allow_Multiple ? (
            <i className="ri-check-line text-success" title="One employee may hold this more than once"></i>
          ) : (
            <i className="ri-close-line text-muted" title="Once per employee at a time"></i>
          )}
          {d.Requires_Details ? (
            <i className="ri-file-text-line ms-2 text-primary" title="Details must be entered on assignment"></i>
          ) : null}
        </td>
        <td className="text-center">{d.Holders}</td>
        <td>
          {d.Is_Active ? (
            <span className="badge bg-success">active</span>
          ) : (
            <span className="badge bg-secondary">inactive</span>
          )}
        </td>
        <td>
          <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
            <li>
              <button type="button" className="green" title="Edit" onClick={() => openEdit(d)}>
                <i className="ri-edit-line"></i>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="blue"
                title={d.Is_Active ? "Deactivate" : "Activate"}
                onClick={() => toggleActive(d)}
              >
                <i className={d.Is_Active ? "ri-toggle-fill" : "ri-toggle-line"}></i>
              </button>
            </li>
            <li>
              <button type="button" className="red" title="Delete" onClick={() => remove(d)}>
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          </ul>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <PayrollMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/Payroll/PayrollDashboard">Payroll</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Additional Duties
                  </li>
                </ol>
              </nav>
              <div className="page-title">Additional Duties / Responsibilities</div>
            </div>
            <div className="col-md-6">
              <div className="page-right-actions">
                <button type="button" className="button button-white button-icon" onClick={openAdd}>
                  Add Responsibility
                  <i className="ri-add-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body">
              <p className="small text-muted mb-3">
                Responsibilities an employee can hold alongside their post, with the monthly allowance each
                carries. Assign them from the employee's profile. Allowances will be applied in payroll
                automatically in the next phase.
              </p>
              <div className="table-responsive">
                <table className="table table-theme table-hover">
                  <thead>
                    <tr>
                      <th>Sr.</th>
                      <th>Responsibility</th>
                      <th className="text-end">Allowance / month</th>
                      <th>Salary type</th>
                      <th className="text-center">Multiple</th>
                      <th className="text-center">Holders</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-3">
                          <Spinner animation="border" role="status" />
                        </td>
                      </tr>
                    ) : (
                      renderRows()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* add / edit */}
      <Modal className="modal-theme" show={showForm} onHide={() => setShowForm(false)} size="lg" centered>
        <Form noValidate validated={validated} onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px" }}>
              {entry.ID ? "Edit responsibility" : "Add responsibility"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="text"
                  required
                  maxLength={100}
                  placeholder="Wing Incharge, Class Teacher …"
                  value={entry.Role_Name}
                  onChange={(e) => set("Role_Name", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Enter the name.</Form.Control.Feedback>
              </div>
              <div className="col-md-4">
                <label className="form-label">Allowance / month (Rs.)</label>
                <Form.Control
                  type="number"
                  min={0}
                  step="1"
                  placeholder="0"
                  value={entry.Allowance_Value}
                  onChange={(e) => set("Allowance_Value", e.target.value)}
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Description</label>
                <Form.Control
                  type="text"
                  maxLength={500}
                  value={entry.Description}
                  onChange={(e) => set("Description", e.target.value)}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Salary type (for payroll)</label>
                <Form.Select value={entry.Salary_Type_ID} onChange={(e) => set("Salary_Type_ID", e.target.value)}>
                  <option value="">-- not linked yet --</option>
                  {salaryTypes.map((t) => (
                    <option key={t.ID} value={t.ID}>
                      {t.DisplayName || t.Name}
                      {t.SType ? ` (${t.SType})` : ""}
                    </option>
                  ))}
                </Form.Select>
                <div className="form-text">The salary line this allowance will post to once payroll is connected.</div>
              </div>
              <div className="col-md-3">
                <label className="form-label">Sort order</label>
                <Form.Control
                  type="number"
                  step="1"
                  value={entry.Sort_Order}
                  onChange={(e) => set("Sort_Order", e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <Form.Select value={entry.Is_Active ? "1" : "0"} onChange={(e) => set("Is_Active", e.target.value === "1")}>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </Form.Select>
              </div>

              <div className="col-md-6">
                <Form.Check
                  type="checkbox"
                  id="duty-allow-multiple"
                  label="One employee may hold this more than once at a time"
                  checked={entry.Allow_Multiple}
                  onChange={(e) => set("Allow_Multiple", e.target.checked)}
                />
                <div className="form-text">e.g. Subject Coordinator for two subjects.</div>
              </div>
              <div className="col-md-6">
                <Form.Check
                  type="checkbox"
                  id="duty-requires-details"
                  label="Details must be entered on assignment"
                  checked={entry.Requires_Details}
                  onChange={(e) => set("Requires_Details", e.target.checked)}
                />
                <div className="form-text">e.g. "Other Additional Duty" must say what it is.</div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="button button-light" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : entry.ID ? "Update" : "Add"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default ManageAdditionalDuties;
